/**
 * @fileoverview Hook for profit by country data from dashboard data.
 * @module shared/hooks/dashboard/useProfitByCountryData
 */

import { useMemo } from 'react'
import { useDashboardData } from './useDashboardData'

/**
 * Profit by country data point.
 */
export interface ProfitByCountryPoint {
  /** Country code */
  country: string
  /** Profit amount */
  profit: number
  /** Revenue amount */
  revenue: number
  /** Cost amount */
  cost: number
  /** Advertising spend amount */
  adSpend: number
  /** Cost of goods sold amount */
  cogs: number
  /** Units sold */
  units: number
  /** Profit margin percentage */
  margin: number
}

/**
 * Hook result for profit by country.
 */
export interface UseProfitByCountryResult {
  /** Country profit data */
  data: ProfitByCountryPoint[]
  /** Loading state */
  isLoading: boolean
  /** Error state */
  isError: boolean
  /** Error message */
  error: string | null
}

/**
 * Hook to get profit by country data from dashboard data.
 *
 * @returns Profit by country data with loading and error states
 * @example
 * ```typescript
 * const { data, isLoading } = useProfitByCountryData();
 * ```
 */
export function useProfitByCountryData(): UseProfitByCountryResult {
  const { salesProductQuery } = useDashboardData()

  const dimString = (d: Record<string, unknown> | undefined, key: string): string | undefined => {
    if (!d) return undefined
    const v = d[key as keyof typeof d]
    return typeof v === 'string' ? v : undefined
  }

  const data = useMemo(() => {
    const dataPoints = salesProductQuery.data?.data || []

    // Group data points by country and aggregate
    const countryMap = new Map<
      string,
      {
        profit: number
        revenue: number
        cost: number
        adSpend: number
        cogs: number
        units: number
      }
    >()

    dataPoints.forEach(dataPoint => {
      const countryName =
        dimString(dataPoint.dimensions as Record<string, unknown>, 'country') || ''
      if (!countryName) return

      const revenue = dataPoint.metrics.sales || 0
      const adSpend = dataPoint.metrics.advertising || 0
      const cogs = dataPoint.metrics.cogs || 0
      const direct = dataPoint.metrics.directCosts || 0
      const units = dataPoint.metrics.quantity || 0
      const cost = adSpend + cogs + direct
      const profit =
        typeof dataPoint.metrics.cm2 === 'number' ? dataPoint.metrics.cm2 : revenue - cost

      const existing =
        countryMap.get(countryName) ||
        { profit: 0, revenue: 0, cost: 0, adSpend: 0, cogs: 0, units: 0 }
      existing.profit += profit
      existing.revenue += revenue
      existing.cost += cost
      existing.adSpend += adSpend
      existing.cogs += cogs
      existing.units += units
      countryMap.set(countryName, existing)
    })

    return Array.from(countryMap.entries()).map(
      ([countryName, totals]): ProfitByCountryPoint => ({
        country: countryName,
        profit: totals.profit,
        revenue: totals.revenue,
        cost: totals.cost,
        adSpend: totals.adSpend,
        cogs: totals.cogs,
        units: totals.units,
        margin: totals.revenue > 0 ? (totals.profit / totals.revenue) * 100 : 0,
      })
    )
  }, [salesProductQuery.data])

  return {
    data,
    isLoading: salesProductQuery.isLoading,
    isError: salesProductQuery.isError,
    error: salesProductQuery.error?.message || null,
  }
}
